import type { CollectibleId } from "@/services/collectibles/collectiblesTypes";
import type { UserId } from "@/services/user/userTypes";
import type { FilterValues } from "@/services/filter/filterTypes";

export const ParameterSlug_MarketplacePrice = "marketplace-price";

// The API has no dedicated flag to mark category parameters — we identify them by slug.
// This is the only way the API currently provides.
export const ParameterSlug_Category = "category";

// Query-string filter keys (never filter-path segments) shared across the search composables.
export enum FilterSlug {
  // Shared across marketplace/collection/collectibles search composables — these are not real
  // elastic filter parameters, so they are kept in the query string, not the filter path.
  Query = "query",
  Sort = "sort",
  // Marketplace-only preset filters (see useUrlFilter's extraQueryStringKeys).
  /** Comma-separated collectible ids, to match the API's accepted form. */
  CollectibleIds = "collectibleIds",
  /** Pins the listing to a single seller by user id (e.g. a deep link from a collector's profile). */
  OwnerId = "ownerId",
  /** The saved search this marketplace listing was opened from: new items get labelled and the search counts as seen. */
  SavedSearch = "savedSearch",
}

/** Comma-separated URL form of a collectible-ids prefilter (deduped); undefined for an empty set so the query param is omitted entirely. */
export const serializeCollectibleIdsParam = (ids: CollectibleId[]): string | undefined => {
  const deduped = [...new Set(ids)];
  return deduped.length > 0 ? deduped.join(",") : undefined;
};

/** Collectible ids from the URL param (string, or array when the param is repeated); non-numeric junk is dropped, ids deduped. */
export const parseCollectibleIdsParam = (value: string | (string | null)[] | null | undefined): CollectibleId[] => {
  const raw = Array.isArray(value) ? value.filter((part): part is string => part !== null) : value ? [value] : [];
  const ids = raw
    .flatMap((part) => part.split(","))
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);
  return [...new Set(ids)];
};

/** Owner id from the URL param (string, or array when the param is repeated); first valid positive integer wins. */
export const parseOwnerIdParam = (value: string | (string | null)[] | null | undefined): UserId | undefined => {
  const raw = Array.isArray(value) ? (value.find((part): part is string => part !== null) ?? undefined) : (value ?? undefined);
  const id = raw !== undefined ? Number(raw.trim()) : NaN;
  return Number.isInteger(id) && id > 0 ? (id as UserId) : undefined;
};

/** First value of a (possibly multi-value) filter field, or undefined if unset. */
export const firstFilterValue = (filterValues: FilterValues | undefined, slug: string): string | undefined => {
  const value = filterValues?.[slug];
  return Array.isArray(value) ? value[0] : value;
};

/** Saved-search id from the URL param; anything that is not a positive integer is ignored. */
export const parseSavedSearchIdParam = (value: string | (string | null)[] | null | undefined): number | undefined => {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== "string") {
    return undefined;
  }
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : undefined;
};
